import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

const STORAGE_KEY = "saloni_bday_reveal_seen";

const STAR_POSITIONS = [
  { top: "12%", left: "18%", size: 22, delay: 0.1 },
  { top: "22%", left: "78%", size: 18, delay: 0.25 },
  { top: "70%", left: "12%", size: 26, delay: 0.35 },
  { top: "78%", left: "82%", size: 20, delay: 0.15 },
  { top: "18%", left: "48%", size: 14, delay: 0.5 },
  { top: "82%", left: "48%", size: 16, delay: 0.6 },
  { top: "40%", left: "8%", size: 12, delay: 0.7 },
  { top: "50%", left: "90%", size: 14, delay: 0.4 },
  { top: "8%", left: "68%", size: 12, delay: 0.55 },
  { top: "88%", left: "30%", size: 18, delay: 0.3 },
  { top: "32%", left: "88%", size: 12, delay: 0.65 },
  { top: "60%", left: "72%", size: 22, delay: 0.2 },
];

const fireBurst = () => {
  const colors = ["#A7C7E7", "#7B9ACC", "#D6E4F0", "#F7F9FC", "#0B192C"];
  confetti({
    particleCount: 160,
    spread: 100,
    startVelocity: 55,
    origin: { y: 0.55 },
    colors,
    scalar: 1.15,
  });
  setTimeout(
    () =>
      confetti({
        particleCount: 100,
        spread: 130,
        origin: { x: 0.15, y: 0.6 },
        colors,
        angle: 60,
      }),
    250
  );
  setTimeout(
    () =>
      confetti({
        particleCount: 100,
        spread: 130,
        origin: { x: 0.85, y: 0.6 },
        colors,
        angle: 120,
      }),
    500
  );
  setTimeout(
    () =>
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.4 },
        colors,
        scalar: 0.85,
      }),
    900
  );
};

export default function BirthdayReveal() {
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    setVisible(true);
    // Small delay so mount is smooth before confetti
    const t = setTimeout(() => fireBurst(), 250);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    if (dismissing) return;
    setDismissing(true);
    localStorage.setItem(STORAGE_KEY, "1");
    // Let framer play the exit animation
    setTimeout(() => setVisible(false), 50);
  };

  // Auto-dismiss after 3s (was 5s — too long on mobile, blocked interactions)
  useEffect(() => {
    if (!visible || dismissing) return;
    const t = setTimeout(dismiss, 3000);
    return () => clearTimeout(t);
  }, [visible, dismissing]);

  // Any scroll or touch also dismisses instantly
  useEffect(() => {
    if (!visible) return;
    const handler = () => dismiss();
    window.addEventListener("scroll", handler, { passive: true, once: true });
    window.addEventListener("touchstart", handler, { passive: true, once: true });
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("touchstart", handler);
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          key="reveal"
          data-testid="birthday-reveal"
          onClick={dismiss}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] bg-[#0B192C] cursor-pointer overflow-hidden text-left"
          style={{ pointerEvents: dismissing ? "none" : "auto" }}
          aria-label="Enter the site"
        >
          {/* Radial glow */}
          <div className="absolute inset-0" style={{
            background:
              "radial-gradient(60% 60% at 50% 50%, rgba(167,199,231,0.28) 0%, rgba(11,25,44,0) 60%)",
          }} />

          {/* Twinkling stars */}
          {STAR_POSITIONS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0, rotate: -30 }}
              animate={{
                opacity: [0, 1, 0.6, 1],
                scale: [0, 1.2, 0.9, 1],
                rotate: 0,
              }}
              transition={{
                duration: 1.2,
                delay: 0.4 + s.delay,
                repeat: Infinity,
                repeatType: "reverse",
                repeatDelay: 0.8 + (i % 3) * 0.2,
              }}
              className="absolute text-[#A7C7E7]"
              style={{ top: s.top, left: s.left }}
            >
              <Star className="fill-[#A7C7E7]" style={{ width: s.size, height: s.size }} />
            </motion.div>
          ))}

          {/* Center content */}
          <div className="relative h-full flex flex-col items-center justify-center px-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-body text-xs sm:text-sm uppercase tracking-[0.5em] text-[#A7C7E7]"
            >
              Happy Birthday
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 40, letterSpacing: "0.5em" }}
              animate={{ opacity: 1, y: 0, letterSpacing: "-0.02em" }}
              transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display font-black text-[#F7F9FC] text-[22vw] sm:text-[16vw] md:text-[13vw] leading-[0.85] tracking-tight"
              style={{
                textShadow: "0 0 60px rgba(167,199,231,0.35), 0 0 120px rgba(123,154,204,0.2)",
              }}
            >
              Bob.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-6 font-hand text-[#A7C7E7] text-3xl sm:text-4xl"
            >
              your day, your world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.4 }}
              className="mt-10 flex items-center gap-2 text-[#D6E4F0]/80 font-body text-xs uppercase tracking-[0.4em]"
            >
              <Sparkles className="w-3.5 h-3.5" />
              tap to enter
              <Sparkles className="w-3.5 h-3.5" />
            </motion.div>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
